// ─────────────────────────────────────────────────────────────────────────────
// NodeMap — the Map View of the Nodes page.
//   This file is composition only: toolbar, the dotted world map, one marker per
//   locatable node, and the legend. The pieces live next door:
//     ./dottedMap  — the offline base SVG + lat/lng → percentage projection
//     ./useZoom    — the zoom ladder and the resulting viewport scale
//     ./MapToolbar · ./NodeMarker · ./MapLegend  — presentation
//
//   ⚠ Coordinate contract: markers are absolutely positioned in percent of the
//   viewport, which is the same box the base SVG fills. That is why .canvas is
//   locked to the SVG's aspect ratio. If the two ever diverge, the SVG gets
//   letterboxed inside the box while the markers keep using the full box, and
//   every node silently drifts off its real location. To change the framing,
//   adjust FIT_SCALE in ./useZoom — never the canvas ratio.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from "react";
import styles from "./NodeMap.module.css";
import { getBaseMap, projectNodes } from "./dottedMap";
import { useZoom } from "./useZoom";
import MapToolbar from "./MapToolbar";
import NodeMarker from "./NodeMarker";
import MapLegend from "./MapLegend";

export default function NodeMap({ nodes = [], selectedId, onSelect, onAddNode, canWrite = false }) {
  const { svg, width, height } = getBaseMap(); // cached singleton, safe to call every render
  const pins = useMemo(() => projectNodes(nodes), [nodes]);
  const activeConnections = useMemo(() => sumActiveCircuits(nodes), [nodes]);
  const zoom = useZoom();

  return (
    <div className={styles.wrap}>
      <MapToolbar
        activeConnections={activeConnections}
        zoom={zoom}
        onAddNode={onAddNode}
        canWrite={canWrite}
      />

      <div className={styles.canvas} style={{ aspectRatio: `${width} / ${height}` }}>
        <div className={styles.viewport} style={{ transform: `scale(${zoom.scale})` }}>
          {svg ? (
            <div className={styles.base} dangerouslySetInnerHTML={{ __html: svg }} />
          ) : (
            <div className={styles.fallback}>Map unavailable</div>
          )}

          {pins.map(({ node, xPct, yPct, color }, i) => (
            <NodeMarker
              key={node.node_id ?? i}
              node={node}
              xPct={xPct}
              yPct={yPct}
              color={color}
              selected={selectedId === node.node_id}
              onSelect={onSelect}
            />
          ))}
        </div>

        <MapLegend />
      </div>
    </div>
  );
}

/**
 * "Active Connections" = the fleet-wide sum of active_circuits.
 * Only nodes that actually report the metric are counted; if none do, returns
 * null so the toolbar shows "—" instead of a 0 that reads like a real reading.
 * (This used to mirror summary.total_users, which is an unrelated metric.)
 */
function sumActiveCircuits(nodes) {
  const reported = nodes.filter((n) => Number.isFinite(n.active_circuits));
  if (reported.length === 0) return null;
  return reported.reduce((sum, n) => sum + n.active_circuits, 0);
}
